/**
Copyright (c) 2022–2026 Podstack.ai
All rights reserved.
Proprietary and Confidential.
Unauthorized copying of this file, via any medium, is strictly prohibited.
This file is part of the Podstack Platform.
*/

import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { format } from 'date-fns';
import { LinkButton, Skeleton, Tag } from '../../components/console/ui';
import { useWallet } from '../../hooks/useWallet';
import BillingExplanationModal from '../../components/billing/BillingExplanationModal';
import ChargesTable from '../../components/billing/ChargesTable';
import GroupedChargesTable from '../../components/billing/GroupedChargesTable';
import { Notice } from '../../components/billing/BillingModal';
import {
  downloadTransactionsCSV,
  formatTimeLeft,
  groupTransactions,
  money,
  moneyCompact,
  USAGE_LABELS,
} from '../../components/billing/billingUtils';
import {
  useBillingContext,
  useExpenditure,
  usePaymentMethods,
  useTransactionGroups,
  useTransactions,
  useUsageByProduct,
} from '../../components/billing/useBilling';
import type { BillingProductKey } from '../../types';

const RECENT_ROWS = 5;

// Always listed in the usage card (design); other products appear when non-zero.
const BASE_PRODUCTS: BillingProductKey[] = ['pods', 'nfs', 'inference', 'training', 'object_storage'];
const RECENT_QUERY = { type: ['debit' as const], page: 1, page_size: RECENT_ROWS };

const BillingOverview: React.FC = () => {
  const { currency, exchangeRate } = useBillingContext();
  const { wallet, isLoading: walletLoading } = useWallet();
  const { cards, isSuccess: cardsLoaded } = usePaymentMethods();
  const { data: exp, isLoading: expLoading } = useExpenditure();
  const { data: txData, isLoading: txLoading } = useTransactions(1);
  const { data: usage, isLoading: usageLoading } = useUsageByProduct();
  const { data: recent, isLoading: recentLoading } = useTransactionGroups(RECENT_QUERY);
  const [showHow, setShowHow] = useState(false);

  const transactions = useMemo(() => txData?.transactions || [], [txData]);
  const groups = useMemo(() => groupTransactions(transactions.filter((t) => t.type === 'debit')), [transactions]);

  const autoPayOn = !!wallet?.auto_debit_enabled;
  const autoPayBroken = autoPayOn && cardsLoaded && cards.length === 0;

  // Preferred: /wallet/usage-by-product (month-to-date, every product).
  // Fallback for older backends: /wallet/expenditure, whose per-product
  // numbers cover the last 7 days (only its total is month-to-date).
  const usageBars = usage
    ? [
        ...BASE_PRODUCTS.map((p) => ({ key: p, label: USAGE_LABELS[p], value: 0 })),
        ...usage.products
          .filter((u) => !BASE_PRODUCTS.includes(u.product) && u.total !== 0)
          .map((u) => ({ key: u.product, label: USAGE_LABELS[u.product] || u.label, value: 0 })),
      ]
        .map((b) => ({ ...b, value: usage.products.find((u) => u.product === b.key)?.total || 0 }))
        .sort((a, b) => b.value - a.value)
    : null;
  const legacyBars = exp
    ? [
        { label: 'QuickPods · Pods', value: exp.container_expenditure || 0 },
        { label: 'QuickPods · NFS', value: exp.storage_expenditure || 0 },
        { label: 'Virtual machines', value: exp.vm_expenditure || 0 },
        { label: 'Object storage', value: exp.bucket_expenditure || 0 },
      ].sort((a, b) => b.value - a.value)
    : [];
  const bars = usageBars || legacyBars;
  const maxBar = Math.max(...bars.map((b) => b.value), 0);
  const monthTotal = usage ? usage.total : exp?.monthly_expenditure || 0;
  const breakdownLoading = usageLoading || (!usage && expLoading);

  return (
    <>
      <div className="flex flex-wrap gap-5">
        <section className="ps-card p-[22px] flex flex-col gap-3.5 flex-[1_1_320px] min-w-0" aria-labelledby="bal">
          <h2 id="bal" className="m-0 text-sm font-medium text-ps-muted">
            Wallet balance
          </h2>
          {walletLoading ? (
            <Skeleton className="h-10 w-48" />
          ) : (
            <div className="text-[36px] leading-tight font-semibold tracking-[-0.01em] text-ps-text break-all">
              {money(wallet?.balance || 0, currency, exchangeRate)}
            </div>
          )}
          {currency === 'USD' && <span className="text-xs text-ps-subtle -mt-2">Rate: 1 USD = ₹{exchangeRate.toFixed(2)}</span>}
          {wallet && (
            <div className="flex gap-2 flex-wrap">
              <Tag tone="info">{wallet.account_type === 'postpaid' ? 'Postpaid' : 'Prepaid'}</Tag>
              <Tag tone={autoPayOn ? 'ok' : 'off'}>{autoPayOn ? 'Auto-pay on' : 'Auto-pay off'}</Tag>
              {wallet.has_running_resources && wallet.hourly_run_rate > 0 && (
                <Tag tone="off">
                  {money(wallet.hourly_run_rate, currency, exchangeRate)}/hr
                  {wallet.estimated_hours_left > 0 ? ` · ~${formatTimeLeft(wallet.estimated_hours_left)} left` : ''}
                </Tag>
              )}
            </div>
          )}
          {autoPayBroken && <Notice>Auto-pay is on but no card is saved, so it can’t charge anything.</Notice>}
          <div className="flex gap-2.5 flex-wrap">
            <LinkButton to="/billing/payments?action=topup">Add funds</LinkButton>
            <LinkButton to="/billing/payments?action=card" variant="secondary">
              Add card
            </LinkButton>
            <LinkButton to="/billing/payments?action=coupon" variant="secondary">
              Redeem coupon
            </LinkButton>
          </div>
          <button type="button" className="ps-link text-[13px] self-start min-h-[32px]" onClick={() => setShowHow(true)}>
            How billing works
          </button>
        </section>

        <section className="ps-card p-[22px] flex flex-col gap-4 flex-[2_1_460px] min-w-0" aria-labelledby="use">
          <div className="flex justify-between items-baseline gap-3 flex-wrap">
            <h2 id="use" className="m-0 text-lg font-medium text-ps-text">
              {format(new Date(), 'MMMM')} usage by product
            </h2>
            {breakdownLoading ? (
              <Skeleton className="h-7 w-24" />
            ) : (
              <span className="text-[22px] font-semibold text-ps-text">{money(monthTotal, currency, exchangeRate, 0)}</span>
            )}
          </div>
          <div className="text-[13px] text-ps-subtle -mt-2">
            {usage ? `Month to date, 1–${format(new Date(), 'd MMM')}` : 'By product, last 7 days'}
          </div>
          {breakdownLoading ? (
            <div className="flex flex-col gap-3">
              {[0, 1, 2, 3].map((i) => (
                <Skeleton key={i} className="h-4" />
              ))}
            </div>
          ) : usage || exp ? (
            <div className="flex flex-col gap-3">
              {bars.map((b) => (
                <div
                  key={b.label}
                  className={`grid grid-cols-[minmax(0,130px)_1fr_80px] sm:grid-cols-[150px_1fr_90px] gap-3 items-center text-sm ${
                    b.value > 0 ? 'text-ps-text' : 'text-ps-muted'
                  }`}
                >
                  <span className="truncate">{b.label}</span>
                  <div className="h-2.5 rounded-full bg-ps-raised">
                    {b.value > 0 && (
                      <div
                        className="h-2.5 rounded-full bg-ps-accent"
                        style={{ width: `${Math.max(1, (b.value / (maxBar || 1)) * 100)}%` }}
                      />
                    )}
                  </div>
                  <span className="text-right">{moneyCompact(b.value, currency, exchangeRate)}</span>
                </div>
              ))}
              {!usage && (
                <p className="m-0 text-xs text-ps-subtle">
                  Inference and TrainPods charges come out of the same wallet and are listed under{' '}
                  <Link to="/billing/transactions" className="ps-link">
                    Transactions
                  </Link>
                  .
                </p>
              )}
            </div>
          ) : (
            <p className="m-0 text-sm text-ps-muted">Usage breakdown isn’t available right now.</p>
          )}
        </section>
      </div>

      <section className="flex flex-col gap-3" aria-labelledby="tx">
        <div className="flex justify-between items-center gap-3 flex-wrap">
          <h2 id="tx" className="m-0 text-lg font-medium text-ps-text">
            Recent charges
          </h2>
          <div className="flex gap-2">
            <LinkButton to="/billing/transactions" variant="secondary" size="sm">
              All transactions
            </LinkButton>
            <button
              type="button"
              className="ps-btn2 ps-btn-sm"
              onClick={() => downloadTransactionsCSV(transactions)}
              disabled={transactions.length === 0}
            >
              Download CSV
            </button>
          </div>
        </div>
        {recentLoading || (recent === null && txLoading) ? (
          <Skeleton className="h-48" />
        ) : recent ? (
          recent.groups.length === 0 ? (
            <div className="ps-card p-6 text-sm text-ps-muted">No charges yet.</div>
          ) : (
            <GroupedChargesTable
              groups={recent.groups}
              currency={currency}
              rate={exchangeRate}
              variant="resource-first"
              expandable={false}
              footer="Per-minute charges are rolled up into one line per resource per day. The CSV export keeps every raw line."
            />
          )
        ) : groups.length === 0 ? (
          <div className="ps-card p-6 text-sm text-ps-muted">No charges yet.</div>
        ) : (
          <ChargesTable
            groups={groups.slice(0, RECENT_ROWS)}
            currency={currency}
            rate={exchangeRate}
            variant="resource-first"
            expandable={false}
            footer="Per-minute charges are rolled up into one line per resource per day. The CSV export keeps every raw line."
          />
        )}
      </section>

      <BillingExplanationModal isOpen={showHow} onClose={() => setShowHow(false)} />
    </>
  );
};

export default BillingOverview;
